import React, { useState, useRef } from "react";
import careersImg from "../assets/careers.png";
import "./Careers.css";

const Careers = () => {
  // ================= STATE =================
  const [showModal, setShowModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState("All");
  
  // State for Resume Upload UI
  const [resumeName, setResumeName] = useState("No file chosen");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "",
    city: "",
    resume: null,
  });

  // CHANGED: Now storing the entire job object instead of just title
  const [selectedJob, setSelectedJob] = useState(null);

  const [heroImage, setHeroImage] = useState(careersImg);

  const heroInputRef = useRef(null);
  const jobListRef = useRef(null);
  const resumeInputRef = useRef(null);

  // ================= HIRING PROCESS =================
  const stepsData = [
    { title: "Explore Opportunities", desc: "Browse and select a suitable role." },
    { title: "Submit Your Application", desc: "Upload your resume and details." },
    { title: "Application Screening", desc: "HR evaluates skills & experience." },
    { title: "Technical Assessment", desc: "Complete test/assignment." },
    { title: "Technical Interview", desc: "Discussion with technical panel." },
    { title: "Managerial Interview", desc: "Meet manager/team lead." },
    { title: "HR Discussion", desc: "Salary expectations & policy details." },
    { title: "Final Selection & Offer", desc: "Receive offer & onboarding info." },
  ];

  // ================= JOB DATA WITH DESCRIPTIONS =================
  const jobData = [
    { 
      id: 1, 
      title: "Full Stack Developer", 
      dept: "Engineering", 
      type: "Full-time",
      description: "We are looking for a skilled Full Stack Developer to join our engineering team. You will be responsible for developing and maintaining web applications, ensuring high performance and responsiveness.",
      requirements: [
        "3+ years of experience with React.js and Node.js.",
        "Proficiency in SQL/NoSQL databases.",
        "Experience with RESTful APIs.",
        "Strong understanding of version control (Git)."
      ]
    },
    { 
      id: 2, 
      title: "Product Designer", 
      dept: "Design", 
      type: "Full-time",
      description: "Join our design team to craft intuitive and beautiful user interfaces. You will work closely with product managers and engineers to deliver high-quality design solutions.",
      requirements: [
        "Proficiency in Figma and Adobe XD.",
        "Strong portfolio demonstrating UI/UX skills.",
        "Experience with design systems.",
        "Understanding of user research methods."
      ]
    },
    { 
      id: 3, 
      title: "React Developer", 
      dept: "Engineering", 
      type: "Remote",
      description: "Seeking a passionate Frontend Developer to specialize in React. You will build modern, responsive web applications and optimize them for performance.",
      requirements: [
        "2+ years of experience with React.js ecosystem.",
        "Knowledge of modern CSS (Tailwind, Styled Components).",
        "Experience with state management libraries (Redux, Context).",
        "Ability to write clean, maintainable code."
      ]
    },
    { 
      id: 4, 
      title: "HR Manager", 
      dept: "Operation", 
      type: "On-site",
      description: "We need an HR Manager to oversee our recruitment, employee relations, and organizational culture. You will be the bridge between management and employees.",
      requirements: [
        "5+ years of experience in Human Resources.",
        "Strong knowledge of labor laws and HR best practices.",
        "Excellent communication and interpersonal skills.",
        "Experience with HR management software."
      ]
    },
    { 
      id: 5, 
      title: "Marketing Lead", 
      dept: "Marketing", 
      type: "Full-time",
      description: "Lead our marketing strategies to drive brand awareness and lead generation. You will manage campaigns, analyze market trends, and oversee the marketing team.",
      requirements: [
        "4+ years of experience in digital marketing.",
        "Proven track record of successful marketing campaigns.",
        "Strong analytical skills and data-driven thinking.",
        "Experience with SEO/SEM and content marketing."
      ]
    },
    { 
      id: 6, 
      title: "DevOps Engineer", 
      dept: "Engineering", 
      type: "Remote",
      description: "Help us improve our deployment pipelines and infrastructure. You will work on automating processes, managing cloud resources, and ensuring system reliability.",
      requirements: [
        "Experience with CI/CD tools (Jenkins, GitLab CI).",
        "Proficiency in cloud platforms (AWS, Azure, GCP).",
        "Knowledge of containerization (Docker, Kubernetes).",
        "Strong scripting skills (Bash, Python)."
      ]
    },
    { 
      id: 7, 
      title: "UX Researcher", 
      dept: "Design", 
      type: "Contract",
      description: "Conduct user research to inform our design decisions. You will run usability tests, interviews, and surveys to gather insights about user needs.",
      requirements: [
        "2+ years of experience in UX research.",
        "Experience with qualitative and quantitative research methods.",
        "Ability to synthesize data into actionable insights.",
        "Excellent presentation skills."
      ]
    }
  ];

  // ================= HANDLERS =================
  const scrollToJobs = () => {
    if (jobListRef.current) jobListRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const filteredJobs =
    activeFilter === "All"
      ? jobData
      : jobData.filter((job) => job.dept === activeFilter);

  const handleFilter = (dept) => setActiveFilter(dept);

  // UPDATED: Now receives the full job object
  const openApplyModal = (job) => {
    setSelectedJob(job);
    setFormData((prev) => ({ ...prev, role: job.title }));
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleHeroUpload = (e) => {
    const file = e.target.files[0];
    if (file) setHeroImage(URL.createObjectURL(file));
  };

  const handleResumeUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, resume: file }));
      setResumeName(file.name);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert("Application Submitted Successfully!");
    setShowModal(false);
    
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      role: "",
      city: "",
      resume: null,
    });
    setResumeName("No file chosen");
  };

  return (
    <div className="careers-page">

      {/* ================= HERO ================= */}
      <section className="careers-hero">
        <div className="hero-left">
          <div className="hero-badge"><span>•</span> Now Hiring</div>

          <h1>Join <span>the team</span></h1>
          <p>
            Vijay Software Solutions is an employee-centered company
            that supports growth & innovation.
          </p>

          <button className="btn-hero" onClick={scrollToJobs}>
            View Open Positions
          </button>
        </div>

        <div className="hero-right">
          <div className="image-container-frame">
            <img src={heroImage} alt="Careers" className="main-image" />

            <button className="upload-btn-hero" onClick={() => heroInputRef.current.click()}>
              Upload
            </button>

            <input
              type="file"
              accept="image/*"
              ref={heroInputRef}
              onChange={handleHeroUpload}
              style={{ display: "none" }}
            />
          </div>

          <div className="floating-badge fb-1">Global Team</div>
          <div className="floating-badge fb-2">Build Skills</div>
        </div>
      </section>

      {/* ================= TIMELINE ================= */}
      <section className="section-padding timeline-section">
        <h2 className="section-title">Hiring Process</h2>

        <div className="timeline-container">
          <div className="timeline-line"></div>

          {stepsData.map((item, index) => (
            <div className="timeline-item" key={index}>
              <div className="tl-number">{String(index + 1).padStart(2, "0")}</div>
              <div className="tl-content">
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= JOB LISTINGS ================= */}
      <section className="section-padding jobs-section" ref={jobListRef}>
        <h2 className="section-title">Open Positions ({filteredJobs.length})</h2>

        <div className="jobs-layout">
          <aside className="job-filter">
            <h3>Departments</h3>
            <ul className="filter-list">
              {["All", "Engineering", "Product", "Design", "Operation", "Marketing"].map((dept) => (
                <li
                  key={dept}
                  className={`filter-item ${activeFilter === dept ? "active" : ""}`}
                  onClick={() => handleFilter(dept)}
                >
                  {dept}
                </li>
              ))}
            </ul>
          </aside>

          <div className="job-list">
            {filteredJobs.map((job) => (
              <div className="job-card" key={job.id} onClick={() => openApplyModal(job)} style={{cursor: "pointer"}}>
                <div className="job-info">
                  <h3>{job.title}</h3>
                  <div className="job-tags">
                    <span className="job-tag type">{job.type}</span>
                    <span className="job-tag">{job.dept}</span>
                  </div>
                </div>

                {/* Stop propagation to avoid double firing openApplyModal */}
                <button 
                  className="apply-btn-small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    openApplyModal(job);
                  }}
                >
                  Apply
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= MODAL ================= */}
      {showModal && selectedJob && (
        <div className="modal-bg open" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setShowModal(false)}>✕</button>

            <h2>
              Apply for <span style={{ color: "var(--primary)" }}>{selectedJob.title}</span>
            </h2>

            {/* NEW: JOB DESCRIPTION SECTION INSIDE MODAL */}
            <div className="job-details-container">
              <div className="jd-section">
                <h3>About the Role</h3>
                <p>{selectedJob.description}</p>
              </div>
              
              <div className="jd-section">
                <h3>Requirements</h3>
                <ul>
                  {selectedJob.requirements.map((req, index) => (
                    <li key={index}>{req}</li>
                  ))}
                </ul>
              </div>
            </div>

            <hr className="modal-divider" />

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>City</label>
                  <input
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Role</label>
                  <select name="role" value={formData.role} onChange={handleInputChange}>
                    <option value="Software Developer">Software Developer</option>
                    <option value="Product Designer">Product Designer</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Resume</label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    ref={resumeInputRef}
                    onChange={handleResumeUpload}
                    style={{ display: "none" }}
                    required
                  />
                  <button
                    type="button"
                    className="upload-resume-btn"
                    onClick={() => resumeInputRef.current.click()}
                  >
                    {resumeName}
                  </button>
                </div>
              </div>

              <button type="submit" className="submit-btn">
                Submit Application
              </button>
            </form>

          </div>
        </div>
      )}

    </div>
  );
};

export default Careers;